import { formWeightsOf } from './weights';
import { CandidateDetailRecord, FormDefinition, FormSubmissionRecord } from '../types/api';

// ── Helpers ───────────────────────────────────────────────────────────────────

/** Strip parenthetical scale hints like "(1-7)" or "(1-10)" and trim whitespace */
function cleanTitle(raw: string): string {
  return raw
    .replace(/\s*\([^)]*\)/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

/** CSV-escape a single cell value */
function escapeCell(value: string): string {
  if (value.includes(',') || value.includes('"') || value.includes('\n') || value.includes('\r')) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}

/** Safely convert a value to a display string */
function toStr(v: unknown): string {
  if (v == null) return '';
  if (typeof v === 'boolean') return v ? 'true' : 'false';
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
}

/** Normalize a CSV header for alias matching — lowercase, strip spaces/underscores/dashes.
 * Mirrors the normalization SummaryPage.tsx already applies when detecting the
 * candidateNumber column, so "Name", "Full Name", "candidate_name" etc. all match. */
function normalizeHeader(h: string): string {
  return h.toLowerCase().replace(/[\s_-]/g, '');
}

/** Look up a CSV-enrichment field by a list of acceptable header aliases,
 * matching case/spacing/punctuation-insensitively instead of requiring the
 * exact key used elsewhere in code (e.g. 'name', 'candidateName'). Returns
 * the first non-empty match, or null if the row has no matching column. */
export function findEnrichedValue(
  row: Record<string, string> | undefined,
  aliases: string[]
): string | null {
  if (!row) return null;
  const normalizedAliases = new Set(aliases.map(normalizeHeader));
  for (const [key, value] of Object.entries(row)) {
    if (normalizedAliases.has(normalizeHeader(key)) && value?.trim()) return value.trim();
  }
  return null;
}

/**
 * Parse the actual form field answers from a submission.
 *
 * Two storage formats exist:
 *   1. Modern: submissionData = { rating: N, comments: '{"fieldKey": value, ...}' }
 *   2. Legacy: submissionData = { fieldKey: value, ... }  (fields stored directly)
 *
 * Returns a flat map of fieldKey → string value.
 */
function parseFieldValues(
  sub: FormSubmissionRecord,
  errorLog: string[]
): Record<string, string> {
  const data = sub.submissionData;

  // Modern format: comments is a JSON string
  const commentsRaw = data.comments;
  if (typeof commentsRaw === 'string' && commentsRaw.trim().startsWith('{')) {
    try {
      const parsed = JSON.parse(commentsRaw) as Record<string, unknown>;
      return Object.fromEntries(
        Object.entries(parsed).map(([k, v]) => [k, toStr(v)])
      );
    } catch {
      errorLog.push(`[WARN] Could not parse comments JSON for submission ${sub.id}`);
    }
  }

  // Comments is already an object
  if (commentsRaw && typeof commentsRaw === 'object') {
    return Object.fromEntries(
      Object.entries(commentsRaw as Record<string, unknown>).map(([k, v]) => [k, toStr(v)])
    );
  }

  // Legacy format: fields stored directly in submissionData (skip known system keys)
  const SYSTEM_KEYS = new Set(['rating', 'position', 'team-size']);
  return Object.fromEntries(
    Object.entries(data)
      .filter(([k]) => !SYSTEM_KEYS.has(k))
      .map(([k, v]) => [k, toStr(v)])
  );
}

// ── Public interface ──────────────────────────────────────────────────────────

export interface ExportResult {
  csv: string;
  errorLog: string[];
  rowCount: number;
  columnCount: number;
}

/**
 * Transform multi-submission candidate data into a flat CSV.
 *
 * Schema:
 *   [Static candidate info]
 *   + per FormDefinition: [Evaluator_Eval{N}] [Rating_Eval{N}] [{Field}_Eval{N}] ...
 *
 * Nmax is calculated per form definition — all candidates that submitted that form.
 */
export interface CandidateRankingInfo {
  rank: string;
  tier: string;
  rankedBy: string;
}

function weightsTextOf(fd: FormDefinition): string | null {
  if (fd.formType !== 'generic') return null;
  const w = formWeightsOf(fd.structure?.structure);
  return w ? w.map(x => `${x.title} ${x.weight}%`).join('; ') : null;
}

export function buildExportCSV(
  candidateDetails: CandidateDetailRecord[],
  formDefinitions: FormDefinition[],
  enrichmentMap?: Map<number, Record<string, string>>,
  rankingMap?: Map<string, CandidateRankingInfo>
): ExportResult {
  const errorLog: string[] = [];

  // ── 1. Extract field schema per form definition ────────────────────────────

  interface FieldSpec {
    fieldKey: string;
    cleanTitle: string;
  }
  interface FormSpec {
    formDef: FormDefinition;
    formLabel: string;
    fields: FieldSpec[];
    weightsText: string | null; // set only for weighted generic forms
  }

  const formSpecs: FormSpec[] = formDefinitions
    .filter(fd => fd.isActive)
    .map(fd => {
      const structArr: any[] = fd.structure?.structure ?? [];
      const fields: FieldSpec[] = [];
      for (const f of structArr.filter((f: any) => f.fieldKey)) {
        fields.push({ fieldKey: f.fieldKey as string, cleanTitle: cleanTitle(f.title ?? f.fieldKey) });
        if (f.type === 'flagblock' && f.textFieldKey) {
          fields.push({ fieldKey: f.textFieldKey as string, cleanTitle: `${cleanTitle(f.title ?? f.fieldKey)} (note)` });
        }
      }
      return {
        formDef: fd,
        formLabel: fd.name,
        fields,
        weightsText: weightsTextOf(fd),
      };
    });
  const assessWeighted = formSpecs.some(fd => fd.weightsText);

  // ── 1b. Build redFlag set from flagblock fields ───────────────────────────

  const formDefLookup = new Map(formDefinitions.map(fd => [fd.id, fd]));
  const redFlagIds = new Set<string>();
  outer:
  for (const cand of candidateDetails) {
    for (const sub of cand.formSubmissions) {
      const fd = formDefLookup.get(sub.formDefinitionId);
      const structFields: Array<{ type: string; fieldKey: string }> = fd?.structure?.structure ?? [];
      const flagKeys = structFields.filter(f => f.type === 'flagblock').map(f => f.fieldKey);
      if (flagKeys.length === 0) continue;

      const raw = sub.submissionData.comments;
      let answers: Record<string, unknown> = {};
      if (typeof raw === 'string' && raw.trim().startsWith('{')) {
        try { answers = JSON.parse(raw) as Record<string, unknown>; } catch { /* ignore */ }
      } else if (raw && typeof raw === 'object') {
        answers = raw as Record<string, unknown>;
      }

      for (const key of flagKeys) {
        const val = answers[key];
        if (val === 1 || val === '1' || val === true || val === 'true') {
          redFlagIds.add(cand.id);
          continue outer;
        }
      }
    }
  }

  // ── 2. Group submissions per form per candidate ────────────────────────────

  // formDefId → candidateId → submissions[]
  const subMap = new Map<string, Map<string, FormSubmissionRecord[]>>();
  for (const fd of formSpecs) {
    subMap.set(fd.formDef.id, new Map());
  }

  for (const cand of candidateDetails) {
    for (const sub of cand.formSubmissions) {
      const byCandidate = subMap.get(sub.formDefinitionId);
      if (!byCandidate) continue; // submission for a form not in current definitions
      const arr = byCandidate.get(cand.id) ?? [];
      arr.push(sub);
      byCandidate.set(cand.id, arr);
    }
  }

  // ── 3. Calculate Nmax per form definition ─────────────────────────────────

  const nmaxMap = new Map<string, number>(); // formDefId → Nmax
  for (const fd of formSpecs) {
    const byCandidate = subMap.get(fd.formDef.id)!;
    let nmax = 0;
    Array.from(byCandidate.values()).forEach(subs => {
      nmax = Math.max(nmax, subs.length);
    });
    nmaxMap.set(fd.formDef.id, nmax);
  }

  // ── 4a. Collect enrichment column names ──────────────────────────────────

  const enrichmentCols: string[] = [];
  if (enrichmentMap && enrichmentMap.size > 0) {
    const colSet = new Set<string>();
    for (const row of Array.from(enrichmentMap.values())) for (const k of Object.keys(row)) colSet.add(k);
    enrichmentCols.push(...Array.from(colSet).sort());
  }

  // ── 4. Build header row ───────────────────────────────────────────────────

  const STATIC_HEADERS = [
    'CandidateNumber',
    'TeamNumber',
    'Status',
    'FirstName',
    'LastName',
    'FullName',
    'CandidatePersonalMatam',
    'FinalCycleScore',
    'FinalSocioScore',
    assessWeighted ? 'FinalAssessmentScore (weighted)' : 'FinalAssessmentScore',
    'FinalInterviewScore',
    'TeamleadRank',
    'Rank',
    'Tier',
    'RankedBy',
    'RedFlag',
    'InactivationReason',
    'InactivationNote',
  ];

  const dynamicHeaders: string[] = [];
  for (const fd of formSpecs) {
    const nmax = nmaxMap.get(fd.formDef.id) ?? 0;
    if (nmax === 0) continue;
    const label = fd.formLabel;
    for (let n = 1; n <= nmax; n++) {
      dynamicHeaders.push(`[${label}] eval${n}_name`);
      dynamicHeaders.push(`[${label}] eval${n}_rating${fd.weightsText ? ' (weighted)' : ''}`);
      for (const field of fd.fields) {
        dynamicHeaders.push(`[${label}] eval${n}_${field.cleanTitle}`);
      }
    }
    if (fd.weightsText) dynamicHeaders.push(`[${label}] weights`);
  }

  const enrichmentHeaders = enrichmentCols.map(c => `[Enriched] ${c}`);
  const allHeaders = [...STATIC_HEADERS, ...dynamicHeaders, ...enrichmentHeaders];

  // ── 5. Build data rows ────────────────────────────────────────────────────

  const rows: string[][] = candidateDetails.map(cand => {
    const row: string[] = [
      toStr(cand.candidateNumber),
      toStr(cand.teamNumber),
      cand.status ?? '',
      cand.firstName ?? '',
      cand.lastName ?? '',
      cand.fullName ?? `${cand.firstName ?? ''} ${cand.lastName ?? ''}`.trim(),
      cand.candidatePersonalMatam != null
        ? String(cand.candidatePersonalMatam)
        : (enrichmentMap?.get(cand.candidateNumber)?.['matam'] ?? enrichmentMap?.get(cand.candidateNumber)?.['candidateMatam'] ?? ''),
      cand.finalCycleScore != null ? Number(cand.finalCycleScore).toFixed(2) : '',
      cand.finalSocioScore != null ? Number(cand.finalSocioScore).toFixed(2) : '',
      (cand as any).finalAssesmentScore != null ? Number((cand as any).finalAssesmentScore).toFixed(2) : '',
      cand.finalInterviewScore != null ? Number(cand.finalInterviewScore).toFixed(2) : '',
      cand.teamleadRank != null ? String(cand.teamleadRank) : '',
      rankingMap?.get(cand.id)?.rank    ?? '',
      rankingMap?.get(cand.id)?.tier    ?? '',
      rankingMap?.get(cand.id)?.rankedBy ?? '',
      redFlagIds.has(cand.id) ? 'true' : 'false',
      cand.inactivationReason ?? '',
      (cand as any).inactivationReasonText ?? '',
    ];

    for (const fd of formSpecs) {
      const nmax = nmaxMap.get(fd.formDef.id) ?? 0;
      if (nmax === 0) continue;
      const submissions = subMap.get(fd.formDef.id)?.get(cand.id) ?? [];

      for (let n = 1; n <= nmax; n++) {
        const sub = submissions[n - 1];

        if (!sub) {
          // Missing eval slot — fill with empty values
          row.push(''); // Evaluator
          row.push(''); // Rating
          for (let i = 0; i < fd.fields.length; i++) row.push('');
          continue;
        }

        const evaluatorName = `${sub.submittedBy.firstName} ${sub.submittedBy.lastName}`.trim();
        row.push(evaluatorName);
        row.push(sub.submissionData.rating != null ? toStr(sub.submissionData.rating) : '');

        const fieldValues = parseFieldValues(sub, errorLog);
        for (const field of fd.fields) {
          row.push(fieldValues[field.fieldKey] ?? '');
        }
      }
      if (fd.weightsText) row.push(fd.weightsText);
    }

    if (enrichmentCols.length > 0) {
      const enriched = enrichmentMap?.get(cand.candidateNumber) ?? {};
      for (const col of enrichmentCols) row.push(enriched[col] ?? '');
    }

    return row;
  });

  // ── 6. Serialize CSV ──────────────────────────────────────────────────────

  const lines = [allHeaders, ...rows].map(row =>
    row.map(cell => escapeCell(String(cell))).join(',')
  );

  return {
    csv: lines.join('\r\n'),
    errorLog,
    rowCount: rows.length,
    columnCount: allHeaders.length,
  };
}

/** Trigger a browser download of a CSV string */
export function downloadCSV(csv: string, filename: string): void {
  const bom = '﻿'; // UTF-8 BOM for Excel Hebrew support
  const blob = new Blob([bom + csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}
